import { useCallback, useEffect, useRef, useState } from 'react';
import Editor, { createEditorStateWithText } from '@draft-js-plugins/editor';
import createToolbarPlugin from '@draft-js-plugins/static-toolbar';
import { LinkButton, linkifyPlugin, linkPlugin } from './links/links';
import { videoPlugin } from './video/video';
import { focusPlugin } from './focus/focus';
import { resizeablePlugin } from './resizeable/resizable';
import { alignmentPlugin } from './alignment/alignment';
import { blockDndPlugin } from './move-block/move-block';
import { imagePlugin } from './image/image';

import { getAsDraftContent } from '../../utils/draft-utils';
import DescriptionWrapper from '../description-wrapper/DescriptionWrapper';
import { TitleButtonItem, SubTitleButtonItem } from './headlines/HeadlinesMenuItem';
import VideoMenuItem from './video/VideoMenuItem';
import ImageMenuItem from './image/ImageMenuItem';
import toolbarStyles from './toolbar/toolbar.module.less';
import buttonStyles from './toolbar/button.module.less';
import separatorStyles from './toolbar/separator.module.less';
import styles from './rich-editor.module.less';
import FormatButtonItems from './format-buttons/FormatButtonItems';
import MoreButtonItem from './more/more-item';
import { UnorderedListButton } from './format-list/FormatListItem';
import { Collapse } from '@sb1/ffe-collapse-react';
import { Tooltip } from './tooltip/Tooltip';

const toolbarPlugin = createToolbarPlugin({
    theme: { toolbarStyles, buttonStyles, separatorStyles },
});

const Toolbar = toolbarPlugin.Toolbar;
const plugins = [
    blockDndPlugin,
    focusPlugin,
    alignmentPlugin,
    resizeablePlugin,
    linkPlugin,
    videoPlugin,
    imagePlugin,
    toolbarPlugin,
    linkifyPlugin,
];

const { AlignmentTool } = alignmentPlugin;

const RichEditor = ({
    onChange,
    className,
    placeholder = 'Skriv inn en fin tekst her',
    handleUploadDescriptionImage,
    text,
    disableMedia = false,
    ariaLabelledBy,
    autoFocus = false,
}) => {
    const editorElement = useRef(null);
    const editorFocusWrapper = useRef(null);
    const [editorState, setEditorState] = useState(getAsDraftContent(text) || createEditorStateWithText(''));
    const [shouldRenderEditor, setShouldRenderEditor] = useState(false);
    const [isMobileToolbarOpen, setIsMobileToolbarOpen] = useState(false);

    const handleChange = useCallback((editorState) => setEditorState(editorState), []);
    const handleBlur = useCallback(() => onChange(editorState), [editorState, onChange]);
    const updateEditorState = useCallback((newState) => setEditorState(newState), []);

    const isClientSide = typeof window !== 'undefined';

    useEffect(() => {
        if (isClientSide) {
            setShouldRenderEditor(true);
        }
    }, [isClientSide]);

    useEffect(() => {
        if (editorFocusWrapper?.current && autoFocus) {
            editorFocusWrapper.current.click();
        }
    }, [autoFocus, editorFocusWrapper]);

    if (!shouldRenderEditor) {
        return null;
    }

    return (
        <div className={[styles.editorContainer, className].join(' ')}>
            <DescriptionWrapper className={styles.descriptionWrapper}>
                <div className={styles.editorWrapper}>
                    <div
                        ref={editorFocusWrapper}
                        role={'button'}
                        onClick={() => editorElement?.current?.focus()}
                        className={styles.editorFocusWrapper}
                    >
                        <Editor
                            editorState={editorState}
                            onChange={handleChange}
                            placeholder={placeholder}
                            onBlur={handleBlur}
                            plugins={plugins}
                            ref={editorElement}
                            ariaLabelledBy={ariaLabelledBy ? ariaLabelledBy : null}
                        />
                    </div>
                    <Toolbar>
                        {(externalProps) => (
                            <>
                                <div className={toolbarStyles.toolbarRow}>
                                    <FormatButtonItems {...externalProps} />
                                    <LinkButton {...externalProps} />
                                    {!disableMedia ? (
                                        <ImageMenuItem
                                            {...externalProps}
                                            editorState={editorState}
                                            updateEditorState={updateEditorState}
                                            uploadImage={handleUploadDescriptionImage}
                                        />
                                    ) : null}
                                    <TitleButtonItem {...externalProps} />
                                    <div className={toolbarStyles.toolbarDesktopRow}>
                                        <SubTitleButtonItem {...externalProps} />
                                        <Tooltip text="Punktliste">
                                            <UnorderedListButton {...externalProps} />
                                        </Tooltip>
                                        {!disableMedia ? <VideoMenuItem {...externalProps} /> : null}
                                    </div>

                                    <MoreButtonItem
                                        isMobileToolbarOpen={isMobileToolbarOpen}
                                        setIsMobileToolbarOpen={setIsMobileToolbarOpen}
                                        {...externalProps}
                                    />
                                </div>
                                <div className={toolbarStyles.toolbarMobileRow}>
                                    <Collapse isOpen={isMobileToolbarOpen}>
                                        <SubTitleButtonItem {...externalProps} />
                                        <UnorderedListButton {...externalProps} />
                                        {!disableMedia ? <VideoMenuItem {...externalProps} /> : null}
                                    </Collapse>
                                </div>
                            </>
                        )}
                    </Toolbar>
                </div>
                <AlignmentTool />
            </DescriptionWrapper>
        </div>
    );
};

export default RichEditor;
